/**
 * Responsibility: show one card's arithmetic in full, so the operator can
 * disagree with an assumption rather than with the conclusion.
 *
 * The single-gem and expected payoffs sit side by side deliberately. They
 * answer different questions - "if this works, what do I make" and "what is
 * the average outcome" - and the two disagreeing is the most useful thing on
 * this panel. A card that looks excellent on the left and negative on the
 * right is a lottery ticket, which is fine as a deliberate choice and
 * expensive as a misunderstanding.
 *
 * The asking prices are shown under the sold ones rather than beside them, to
 * keep the visual hierarchy honest: they are context, not inputs.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Opportunity, Payoff } from '../../api/client';
import { PriceCell } from './PriceCell';
import { VerifyLinks } from './VerifyLinks';
import { money, multiple, percent, signedMoney, toNumber, trend } from './money';

type OpportunityDetailProps = {
  row: Opportunity | null;
  onClose: () => void;
  onEditPopulation: (row: Opportunity) => void;
};

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: strong ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function PayoffPanel({
  title,
  subtitle,
  payoff,
}: {
  title: string;
  subtitle: string;
  payoff: Payoff | null;
}) {
  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>

      {!payoff ? (
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1.5 }}>
          Needs PSA population counts to compute.
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          <Line label={`${payoff.cards_bought} raw cards`} value={money(payoff.raw_outlay)} />
          <Line label="Grading" value={money(payoff.grading_outlay)} />
          <Line label="Shipping both ways" value={money(payoff.shipping_outlay)} />
          <Divider sx={{ my: 0.5 }} />
          <Line label="Total cost" value={money(payoff.total_cost)} />
          <Line label="Gross sale" value={money(payoff.gross_proceeds)} />
          <Line label="Marketplace fees" value={`-${money(payoff.selling_fees)}`} />
          <Divider sx={{ my: 0.5 }} />
          <Line label="Profit" value={signedMoney(payoff.profit)} strong />
          <Line label="Return on outlay" value={percent(payoff.roi)} />
        </Stack>
      )}
    </Box>
  );
}

export function OpportunityDetail({ row, onClose, onEditPopulation }: OpportunityDetailProps) {
  if (!row) return null;

  const gemRate = row.population?.gem_rate;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{row.card_name}</span>
          {row.set_name ? (
            <Chip size="small" variant="outlined" label={row.set_name} />
          ) : null}
          {row.card_number ? (
            <Chip size="small" variant="outlined" label={row.card_number} />
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          {row.confidence_notes.length > 0 ? (
            <Alert severity={row.is_falling_sharply ? 'error' : 'warning'}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {row.confidence_notes.map((note) => (
                  <Typography key={note} component="li" variant="body2">
                    {note}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : (
            <Alert severity="success">
              Sold prices on both sides, a deep enough sample, and the price is holding.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              What it trades at
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Raw
                </Typography>
                <PriceCell price={row.raw_price} absent="none sold" />
                <PriceCell price={row.raw_asking_price} absent="no listings" />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  PSA 10
                </Typography>
                <PriceCell price={row.psa10_price} absent="none sold" />
                <PriceCell price={row.psa10_asking_price} absent="no listings" />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  PSA 9
                </Typography>
                <PriceCell price={row.psa9_price} absent="none sold" />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Graded vs raw
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {multiple(row.multiple)}
                </Typography>
                <Typography
                  variant="caption"
                  color={row.is_falling_sharply ? 'error.main' : 'text.secondary'}
                >
                  PSA 10 {trend(row.trend_ratio)}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Divider />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ minWidth: 0 }}>
            <PayoffPanel
              title="If one of them gems"
              subtitle="Your framing: buy the batch, assume exactly one comes back a 10."
              payoff={row.single_gem}
            />
            <PayoffPanel
              title="On average"
              subtitle={
                gemRate
                  ? `At this card's actual gem rate of ${percent(gemRate)}.`
                  : 'Needs a population report.'
              }
              payoff={row.expected}
            />
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Check these numbers
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              The exact searches these prices came from. Worth a glance before buying: the most
              likely error here is a search that matched a different printing.
            </Typography>
            <VerifyLinks card={row} />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Population
            </Typography>
            {row.population ? (
              <Stack spacing={0.5}>
                <Line label="PSA 10s in existence" value={String(row.population.cert_count_10)} />
                <Line label="PSA 9s" value={String(row.population.cert_count_9)} />
                <Line label="Total submissions" value={String(row.population.graded_total)} />
                <Line label="Gem rate" value={percent(row.population.gem_rate)} />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No counts recorded. Without them there is no gem rate, so only the best case can be
                computed - and the best case flatters every card.
              </Typography>
            )}
            <Button size="small" sx={{ mt: 1 }} onClick={() => onEditPopulation(row)}>
              {row.population ? 'Update counts' : 'Enter counts from psacard.com'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Buy at or below {money(row.single_gem.break_even_raw_price)} per raw card. Above that,
            this stops paying even if one gems.
            {(toNumber(row.single_gem.break_even_raw_price) ?? 0) > 0 ? '' : ' It does not pay at any price.'}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
